/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { CloudAppType } from '@modules/ui-types'

type DocLinkDefinition = {
  // will apply to any adminconsole or userconsole, respectively, unless overriden as below
  adminconsole?: string
  userconsole?: string
} & Partial<{
  // override the general adminconsole and userconsole versions as needed
  [key in CloudAppType]: string
}>

const docLinks = {
  activeDirectoryProviderGeneralSettingsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/active-directory#create-ad-profiles',
  },
  activeDirectoryProviderCaCertsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/active-directory#prepare-ad-certificates',
  },
  activeDirectoryProviderBindCredentialsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/active-directory#supply-ad-bind-credentials',
  },
  activeDirectoryProviderUserSearchDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/active-directory#ad-search-mode',
  },
  activeDirectoryProviderGroupSearchDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/active-directory#ad-search-groups',
  },
  activeDirectoryProviderRoleMappingDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/active-directory#ad-role-mapping',
  },
  activeDirectoryProviderAdvancedDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/active-directory#ad-custom-configuration',
  },
  addCapacityDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/maintenance/ece/scale-out-installation',
  },
  addEceLicenseDocLink: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/license/manage-your-license-in-ece',
  },
  apiReferenceDocLink: {
    adminconsole: 'https://www.elastic.co/docs/reference/cloud/cloud-enterprise/restful-api',
    userconsole: 'https://www.elastic.co/docs/reference/cloud/cloud-hosted/ec-api-restful',
  },
  apmUserSettingsDocLink: {
    adminconsole: 'https://www.elastic.co/docs/solutions/observability/apm/configure-apm-server',
    userconsole: 'https://www.elastic.co/docs/solutions/observability/apm/configure-apm-server',
  },
  appsearchUserSettingsDocLink: {
    adminconsole:
      'https://www.elastic.co/guide/en/cloud-enterprise/3.7/ece-manage-appsearch-settings.html',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/edit-stack-settings',
  },
  autoscalingDocLink: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/autoscaling#autoscaling-factors',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/autoscaling#autoscaling-factors',
  },
  autoscalingEnableDocLink: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/autoscaling#autoscaling-enable',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/autoscaling#autoscaling-enable',
  },
  autoscalingUpdateSettingsLink: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/autoscaling#autoscaling-update',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/autoscaling#autoscaling-update',
  },
  cloudIdDocLink: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/find-cloud-id',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/connect-elasticsearch',
  },
  clusterPerfMetricsMonitoringDocLink: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/monitor',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/ece-ech-stack-monitoring',
  },
  configureAllocatorTagsLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/ece-configuring-ece-tag-allocators',
  },
  createDeployment: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/create-an-elastic-cloud-hosted-deployment',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/create-deployment',
  },
  customizeDeployment: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/customize-deployment',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/configure',
  },
  crossClusterRestoreDocLink: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore#restore-snapshot',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore#restore-snapshot',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore#restore-snapshot',
  },
  configureKeystore: {
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/security/secure-settings',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/security/secure-settings',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/security/secure-settings',
  },
  configureSettingsDocLink: {
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/configure',
  },
  deploymentHelpNextSteps: {
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/heroku',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/working-with-deployments',
  },
  enableMonitoringDocLink: {
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/ece-ech-stack-monitoring',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/ece-ech-stack-monitoring',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/ece-ech-stack-monitoring',
  },
  enterpriseSearchUserSettingsDocLink: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/edit-stack-settings',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/edit-stack-settings-enterprise',
  },
  upgradeAppSearchDocLink: {
    userconsole:
      'https://www.elastic.co/guide/en/cloud/current/ec-enable-enterprise-search.html#ec-upgrade-appsearch2',
  },
  esCertutilParamsDocLink: {
    adminconsole: 'https://www.elastic.co/docs/reference/elasticsearch/command-line-tools/certutil',
    userconsole: 'https://www.elastic.co/docs/reference/elasticsearch/command-line-tools/certutil',
    'heroku-userconsole':
      'https://www.elastic.co/docs/reference/elasticsearch/command-line-tools/certutil',
  },
  esUserSettingsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/edit-stack-settings',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/edit-stack-settings',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/edit-stack-settings',
  },
  elasticsearchUserSettingsDocLink: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/edit-stack-settings',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/edit-stack-settings',
  },
  faultToleranceDocLink: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/production-guidance/elasticsearch-in-production-environments#ha',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/ece-ha',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/production-guidance/elasticsearch-in-production-environments#ha',
  },
  generateCertificateAuthorityDocLink: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/security/set-up-basic-security',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/security/set-up-basic-security',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/security/set-up-basic-security',
  },
  gettingStartedDocLink: {
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/heroku',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/working-with-deployments',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/cloud-hosted',
    'saas-adminconsole':
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/cloud-hosted',
  },
  helpDocLink: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise',
    'heroku-userconsole': 'https://www.elastic.co/guide/en/cloud-heroku/current/index.html',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/cloud-hosted',
  },
  securityDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cluster-or-deployment-auth',
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/security',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/users-roles/cluster-or-deployment-auth',
  },
  customBundlesDocLink: {
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/upload-custom-plugins-bundles',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/upload-custom-plugins-bundles',
  },
  accountBillingContactsDocLink: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/update-billing-operational-contacts',
    'saas-adminconsole':
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/update-billing-operational-contacts',
  },
  accountWatcherDocLink: {
    userconsole:
      'https://www.elastic.co/docs/explore-analyze/alerts-cases/watcher#ec-watcher-whitelist',
  },
  mfaDocLink: {
    userconsole: 'https://www.elastic.co/docs/cloud-account/multifactor-authentication',
  },
  accountBillingDetailsDocLink: {},
  accountBillingGcpDocLink: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/google-cloud-platform-marketplace#billing-gcp-account-change',
  },
  awsMarketplace: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/aws-marketplace',
  },
  azureMarketplace: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/azure-native-isv-service',
  },
  gcpMarketplace: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/google-cloud-platform-marketplace',
  },
  indexManagementDocLink: {
    userconsole:
      'https://www.elastic.co/docs/manage-data/lifecycle/index-lifecycle-management/migrate-index-management',
    adminconsole:
      'https://www.elastic.co/guide/en/cloud-enterprise/3.7/ece-configure-index-management.html',
  },
  indexManagementTemplatesDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/ece-configure-templates-index-management',
  },
  kibanaUserSettingsDocLink: {
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/edit-stack-settings',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/edit-stack-settings',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/edit-stack-settings-kibana',
  },
  apiConsoleDocLink: {
    userconsole: 'https://www.elastic.co/docs/reference/cloud/cloud-hosted/ec-api-console',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/tools-apis',
    'heroku-userconsole': 'https://www.elastic.co/docs/reference/cloud/cloud-hosted/ec-api-console',
  },
  organizationsDocLink: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/cloud-organization',
  },
  organizationsAllowedDomains: {
    userconsole:
      'https://www.elastic.co/docs/explore-analyze/alerts-cases/alerts/notifications-domain-allowlist',
  },
  ldapProviderGeneralSettingsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/ldap#create-ldap-profiles',
  },
  ldapProviderCaCertsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/ldap#prepare-ldap-certificates',
  },
  ldapProviderBindCredentialsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/ldap#supply-ldap-bind-credentials',
  },
  ldapProviderSearchModeDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/ldap#ldap-search-mode',
  },
  ldapProviderGroupSearchDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/ldap#ldap-search-groups',
  },
  ldapProviderRoleMappingDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/ldap#ldap-role-mapping',
  },
  ldapProviderAdvancedDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/ldap#ldap-custom-configuration',
  },
  jvmMemoryPressure: {
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/monitor/ec-memory-pressure',
    adminconsole: 'https://www.elastic.co/guide/en/cloud-enterprise/3.7/ece-memory-pressure.html',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/monitor/ec-memory-pressure',
  },
  manageElasticStackDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/manage-elastic-stack-versions',
  },
  manageSnapshotReposDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/cloud-enterprise',
  },
  manageSystemPasswords: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/manage-system-passwords',
  },
  installNewHosts: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/install-ece-on-additional-hosts',
  },
  separateRunnerRolesLink: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/ece-roles',
  },
  manageTlsCertsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/security/secure-your-elastic-cloud-enterprise-installation/manage-security-certificates',
  },
  migrateDataReindex: {
    userconsole: 'https://www.elastic.co/docs/manage-data/migrate#reindex-remote',
  },
  migrateDataRestore: {
    userconsole: 'https://www.elastic.co/docs/manage-data/migrate#restore-snapshots',
  },
  monitoringEceDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/monitor/orchestrators/ece-monitoring-ece-access',
  },
  moveNodesDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/maintenance/ece/move-nodes-instances-from-allocators',
  },
  phoneHome: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/statistics-collected-by-cloud-enterprise',
  },
  pluginsDocLink: {
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/add-plugins-extensions',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/add-plugins-extensions',
    adminconsole: 'https://www.elastic.co/docs/reference/elasticsearch/plugins/plugin-management',
  },
  samlProviderGeneralSettingsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/saml#create-saml-profiles',
  },
  samlProviderAttributeMappingDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/saml#saml-attributes',
  },
  samlProviderRoleMappingDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/saml#saml-role-mapping',
  },
  samlProviderAdvancedDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/saml#saml-custom-configuration',
  },
  samlProviderSslConfigurationDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/saml#saml-ssl-certificates',
  },
  samlProviderSigningConfigurationDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/saml#configure-saml-signing-certificates',
  },
  samlProviderEncryptionConfigurationDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cloud-enterprise-orchestrator/saml#encrypt-saml',
  },
  snapshotRetentionDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore#change-retention-period',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore#change-retention-period',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore#change-retention-period',
  },
  templatesDocLink: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/create-an-elastic-cloud-hosted-deployment',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/deployment-templates',
  },
  versionPolicyLink: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/available-stack-versions',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/manage-elastic-stack-versions',
  },
  createDeploymentDocLink: { userconsole: 'https://www.elastic.co/docs/solutions' },
  templateMigration: {
    userconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
  },
  upgradingDocLink: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/upgrade/deployment-or-cluster',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/upgrade/deployment-or-cluster',
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/upgrade/deployment-or-cluster',
  },
  whereMonitoringIsSentDocLink: {
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/ece-ech-stack-monitoring#enable-logging-and-monitoring',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/ece-ech-stack-monitoring#enable-logging-and-monitoring',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/ece-ech-stack-monitoring#enable-logging-and-monitoring',
  },
  resetElasticPassword: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cluster-or-deployment-auth/manage-elastic-user-cloud',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cluster-or-deployment-auth/manage-elastic-user-cloud',
  },
  apiKeysDocLink: {
    userconsole: 'https://www.elastic.co/docs/reference/cloud/cloud-hosted/ec-api-restful',
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/api-keys/elastic-cloud-enterprise-api-keys#ece-api-keys',
  },
  machineLearningDocLink: {
    adminconsole: 'https://www.elastic.co/docs/explore-analyze/machine-learning/anomaly-detection',
    userconsole: 'https://www.elastic.co/docs/explore-analyze/machine-learning/anomaly-detection',
    'heroku-userconsole':
      'https://www.elastic.co/docs/explore-analyze/machine-learning/anomaly-detection',
  },
  graphDocLink: {
    adminconsole: 'https://www.elastic.co/docs/explore-analyze/visualize/graph',
    userconsole: 'https://www.elastic.co/docs/explore-analyze/visualize/graph',
    'heroku-userconsole': 'https://www.elastic.co/docs/explore-analyze/visualize/graph',
  },
  upgradeAssistantDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/upgrade/prepare-to-upgrade/upgrade-assistant',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/upgrade/prepare-to-upgrade/upgrade-assistant',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/upgrade/prepare-to-upgrade/upgrade-assistant',
  },
  officialPluginsDocLink: {
    adminconsole: 'https://www.elastic.co/docs/reference/elasticsearch/plugins',
    userconsole: 'https://www.elastic.co/docs/reference/elasticsearch/plugins',
    'heroku-userconsole': 'https://www.elastic.co/docs/reference/elasticsearch/plugins',
  },
  officialPluginsAuthorHelpDocLink: {
    adminconsole: 'https://www.elastic.co/docs/extend/elasticsearch',
    userconsole: 'https://www.elastic.co/docs/extend/elasticsearch',
    'heroku-userconsole': 'https://www.elastic.co/docs/extend/elasticsearch',
  },
  securityDlsFlsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cluster-or-deployment-auth/controlling-access-at-document-field-level',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/users-roles/cluster-or-deployment-auth/controlling-access-at-document-field-level',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/users-roles/cluster-or-deployment-auth/controlling-access-at-document-field-level',
  },
  snapshotsDocLink: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore',
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore',
  },
  shardsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/reference/elasticsearch/configuration-reference/cluster-level-shard-allocation-routing-settings#disk-based-shard-allocation',
    userconsole:
      'https://www.elastic.co/docs/reference/elasticsearch/configuration-reference/cluster-level-shard-allocation-routing-settings#disk-based-shard-allocation',
    'heroku-userconsole':
      'https://www.elastic.co/docs/reference/elasticsearch/configuration-reference/cluster-level-shard-allocation-routing-settings#disk-based-shard-allocation',
  },
  elasticsearchGettingStartedDocLink: {
    adminconsole: 'https://www.elastic.co/docs/solutions/search/get-started',
    userconsole: 'https://www.elastic.co/docs/solutions/search/get-started',
    'heroku-userconsole': 'https://www.elastic.co/docs/solutions/search/get-started',
  },
  kibanaGettingStartedDocLink: {},
  apmOverviewDocLink: {
    adminconsole: 'https://www.elastic.co/docs/solutions/observability/apm',
    userconsole: 'https://www.elastic.co/docs/solutions/observability/apm',
    'heroku-userconsole': 'https://www.elastic.co/docs/solutions/observability/apm',
  },
  kibanaIntroductionDocLink: {
    adminconsole: 'https://www.elastic.co/docs/get-started/the-stack',
    userconsole: 'https://www.elastic.co/docs/get-started/the-stack',
    'heroku-userconsole': 'https://www.elastic.co/docs/get-started/the-stack',
  },
  elasticsearchIntroductionDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/reference/elasticsearch/configuration-reference/node-settings',
    userconsole:
      'https://www.elastic.co/docs/reference/elasticsearch/configuration-reference/node-settings',
    'heroku-userconsole':
      'https://www.elastic.co/docs/reference/elasticsearch/configuration-reference/node-settings',
  },
  planForProduction: {
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/production-guidance/elasticsearch-in-production-environments',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/ece-ha',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/production-guidance/elasticsearch-in-production-environments',
  },
  ingestIntroductionDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/reference/elasticsearch/configuration-reference/node-settings',
    userconsole:
      'https://www.elastic.co/docs/reference/elasticsearch/configuration-reference/node-settings',
    'heroku-userconsole':
      'https://www.elastic.co/docs/reference/elasticsearch/configuration-reference/node-settings',
  },
  faqGettingStarted: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/cloud-hosted',
  },
  keepingYourDeploymentHealthy: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/monitor',
  },
  kibanaMonitoringDataLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/kibana-monitoring-data',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/kibana-monitoring-data',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/monitor/stack-monitoring/kibana-monitoring-data',
  },
  esCatIndices: {
    adminconsole:
      'https://www.elastic.co/docs/api/doc/elasticsearch/operation/operation-cat-indices',
    userconsole:
      'https://www.elastic.co/docs/api/doc/elasticsearch/operation/operation-cat-indices',
    'heroku-userconsole':
      'https://www.elastic.co/docs/api/doc/elasticsearch/operation/operation-cat-indices',
  },
  esFailedShards: {
    adminconsole:
      'https://www.elastic.co/docs/api/doc/elasticsearch/operation/operation-cluster-reroute#_retry_failed_shards',
    userconsole:
      'https://www.elastic.co/docs/api/doc/elasticsearch/operation/operation-cluster-reroute#_retry_failed_shards',
    'heroku-userconsole':
      'https://www.elastic.co/docs/api/doc/elasticsearch/operation/operation-cluster-reroute#_retry_failed_shards',
  },
  shield: {
    adminconsole: 'https://www.elastic.co/guide/en/shield/current/index.html',
    userconsole: 'https://www.elastic.co/guide/en/shield/current/index.html',
    'heroku-userconsole': 'https://www.elastic.co/guide/en/shield/current/index.html',
  },
  shieldDefiningRoles: {
    adminconsole: 'https://www.elastic.co/guide/en/shield/current/defining-roles.html',
    userconsole: 'https://www.elastic.co/guide/en/shield/current/defining-roles.html',
    'heroku-userconsole': 'https://www.elastic.co/guide/en/shield/current/defining-roles.html',
  },
  esJvmSection: {
    adminconsole:
      'https://www.elastic.co/guide/en/elasticsearch/guide/current/_monitoring_individual_nodes.html#_jvm_section',
    userconsole:
      'https://www.elastic.co/guide/en/elasticsearch/guide/current/_monitoring_individual_nodes.html#_jvm_section',
    'heroku-userconsole':
      'https://www.elastic.co/guide/en/elasticsearch/guide/current/_monitoring_individual_nodes.html#_jvm_section',
  },
  manageTrafficFiltersIp: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/security/ip-filtering-cloud',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/security/ip-filtering-cloud',
  },
  manageTrafficFiltersVpc: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/security/aws-privatelink-traffic-filters',
  },
  manageTrafficFiltersVnet: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/security/azure-private-link-traffic-filters',
  },
  manageTrafficFiltersPsc: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/security/gcp-private-service-connect-traffic-filters',
  },
  manageTrafficFiltersVpcHowToUse: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/security/aws-privatelink-traffic-filters#how-to-use',
  },
  manageTrafficFiltersVpcFindEndpoint: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/security/aws-privatelink-traffic-filters#find-your-endpoint',
  },
  manageTrafficFiltersVpcFindResourceName: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/security/azure-private-link-traffic-filters#find-your-resource-name',
  },
  manageTrafficFiltersVpcFindResourceId: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/security/azure-private-link-traffic-filters#find-your-resource-id',
  },
  manageTrafficFiltersPscFindEndpoint: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/security/gcp-private-service-connect-traffic-filters#find-your-psc-connection-id',
  },
  configureDeploymenTrafficFilters: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/security/traffic-filtering',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/security/traffic-filtering',
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/security/traffic-filtering',
  },
  readonlyAppSearchEnterpriseSearch: {
    userconsole:
      'https://www.elastic.co/guide/en/cloud/current/ec-enable-enterprise-search.html#ec-appsearch-readonly2',
  },
  trustManagement: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/remote-clusters/ec-enable-ccs',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/remote-clusters/ece-enable-ccs',
  },
  trustManagementAddRelationship: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/remote-clusters/ece-enable-ccs',
  },
  trustNewCaDocLink: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/security/different-ca',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/security/different-ca',
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/security/different-ca',
  },
  changeEceApiUrl: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/cloud-enterprise/change-ece-api-url',
  },
  subscriptionLevels: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/manage-subscription',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/manage-subscription',
  },
  accountBilling: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing',
  },
  billingECU: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/ecu',
    'saas-adminconsole': 'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/ecu',
  },
  billingFAQ: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/billing-faq',
    'saas-adminconsole':
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/billing-faq',
  },
  billingPrepaidConsumptionModel: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/billing-models#prepaid-consumption',
    'saas-adminconsole':
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/billing-models#prepaid-consumption',
  },
  annualContractsFAQ: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/billing-faq#faq-annualprepaid',
  },
  billingCalculationFAQ: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/cloud-hosted-deployment-billing-dimensions',
    'saas-adminconsole':
      'https://www.elastic.co/docs/deploy-manage/cloud-organization/billing/cloud-hosted-deployment-billing-dimensions',
  },
  stopBillingCharges: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/uninstall/delete-a-cloud-deployment',
    'saas-adminconsole':
      'https://www.elastic.co/docs/deploy-manage/uninstall/delete-a-cloud-deployment',
  },
  entSearchStorageIncrease: {
    adminconsole:
      'https://www.elastic.co/guide/en/enterprise-search/current/upgrading-from-enterprise-search-7-11-and-earlier.html',
    userconsole:
      'https://www.elastic.co/guide/en/enterprise-search/current/upgrading-from-enterprise-search-7-11-and-earlier.html',
    'heroku-userconsole':
      'https://www.elastic.co/guide/en/enterprise-search/current/upgrading-from-enterprise-search-7-11-and-earlier.html',
  },
  nodeTypesDocLink: {
    userconsole: 'https://www.elastic.co/docs/reference/cloud/cloud-hosted/ec-api-node-types',
    adminconsole:
      'https://www.elastic.co/docs/reference/cloud/cloud-enterprise/changes-to-index-allocation-api',
  },
  migrateCCS: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/remote-clusters/ec-migrate-ccs',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/remote-clusters/ece-migrate-ccs',
  },
  enableCCS: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/remote-clusters/ec-enable-ccs',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/remote-clusters/ece-enable-ccs',
  },
  fleetOverview: {
    adminconsole: 'https://www.elastic.co/docs/reference/fleet',
    userconsole: 'https://www.elastic.co/docs/reference/fleet',
    'heroku-userconsole': 'https://www.elastic.co/docs/reference/fleet',
  },
  frozenTier: {
    adminconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
    userconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
    'heroku-userconsole': 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
  },
  hotTier: {
    adminconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
    userconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
    'heroku-userconsole': 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
  },
  warmTier: {
    adminconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
    userconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
    'heroku-userconsole': 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
  },
  coldTier: {
    adminconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
    userconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
    'heroku-userconsole': 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
  },
  apmServerOverview: {
    adminconsole: 'https://www.elastic.co/guide/en/apm/server/7.15/index.html',
    userconsole: 'https://www.elastic.co/guide/en/apm/server/7.15/index.html',
    'heroku-userconsole': 'https://www.elastic.co/guide/en/apm/server/7.15/index.html',
  },
  removingADataTier: {
    userconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
    adminconsole: 'https://www.elastic.co/docs/manage-data/lifecycle/data-tiers',
  },
  vcpuBoost: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/ec-vcpu-boost-instance',
  },
  remoteClusterCompatibility: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/remote-clusters/remote-clusters-self-managed',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/remote-clusters/remote-clusters-self-managed',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/remote-clusters/remote-clusters-self-managed',
  },
  remoteClusters: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/remote-clusters/ec-enable-ccs',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/remote-clusters/ece-enable-ccs',
  },
  manageCertificatesDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/security/secure-your-elastic-cloud-enterprise-installation/manage-security-certificates',
  },
  searchableSnapshotsDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/searchable-snapshots',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/searchable-snapshots',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/searchable-snapshots',
  },
  searchableSnapshotsFullyMountedDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/searchable-snapshots#fully-mounted',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/searchable-snapshots#fully-mounted',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/searchable-snapshots#fully-mounted',
  },
  searchableSnapshotsILMDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/reference/elasticsearch/index-lifecycle-actions/ilm-searchable-snapshot',
    userconsole:
      'https://www.elastic.co/docs/reference/elasticsearch/index-lifecycle-actions/ilm-searchable-snapshot',
    'heroku-userconsole':
      'https://www.elastic.co/docs/reference/elasticsearch/index-lifecycle-actions/ilm-searchable-snapshot',
  },
  resilientClusters: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/production-guidance/availability-and-resilience/resilience-in-larger-clusters',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/production-guidance/availability-and-resilience/resilience-in-larger-clusters',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/production-guidance/availability-and-resilience/resilience-in-larger-clusters',
  },
  snapshotRestoreAcrossClusters: {
    userconsole: 'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore',
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore',
  },
  snapshotRestoreVersionMatrix: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore',
    'heroku-userconsole': 'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore',
  },
  troubleshootingClusterIssues: {
    adminconsole: 'https://www.elastic.co/docs/troubleshoot/elasticsearch',
    userconsole: 'https://www.elastic.co/docs/troubleshoot/elasticsearch',
    'heroku-userconsole': 'https://www.elastic.co/docs/troubleshoot/elasticsearch',
  },
  apiKeysKibanaLink: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/api-keys/elasticsearch-api-keys',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/api-keys/elasticsearch-api-keys',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/api-keys/elasticsearch-api-keys',
  },
  apiKeysElasticsearchLink: {
    adminconsole:
      'https://www.elastic.co/docs/api/doc/elasticsearch/operation/operation-security-create-api-key',
    userconsole:
      'https://www.elastic.co/docs/api/doc/elasticsearch/operation/operation-security-create-api-key',
    'heroku-userconsole':
      'https://www.elastic.co/docs/api/doc/elasticsearch/operation/operation-security-create-api-key',
  },
  manageProject: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/project-settings',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/project-settings',
  },
  manageProjectFeatures: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/project-settings#project-features-add-ons',
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/project-settings#project-features-add-ons',
  },
  searchableSnapshotsDepedencyDocLink: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/ece-restore-snapshots-containing-searchable-snapshots-indices-across-clusters',
  },
  azureDedicatedRepo: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/repository-isolation-on-azure',
  },
  snapshotRepositoryMigrationAwsGcp: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/tools/snapshot-and-restore/repository-isolation-on-aws-gcp',
  },
  configChangeErrors: {
    adminconsole:
      'https://www.elastic.co/docs/troubleshoot/deployments/cloud-enterprise/node-bootlooping',
    userconsole: 'https://www.elastic.co/docs/troubleshoot/monitoring/node-bootlooping',
  },
  deploymentMoveNode: {
    adminconsole:
      'https://www.elastic.co/docs/deploy-manage/maintenance/ece/move-nodes-instances-from-allocators',
    userconsole: 'https://www.elastic.co/docs/troubleshoot/monitoring/node-moves-outages',
    'saas-adminconsole': 'https://www.elastic.co/docs/troubleshoot/monitoring/node-moves-outages',
  },
  accessKibana: {
    userconsole:
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/access-kibana#kibana-port',
    'saas-adminconsole':
      'https://www.elastic.co/docs/deploy-manage/deploy/elastic-cloud/access-kibana#kibana-port',
  },
  appSearchWorkplaceDocs: {
    adminconsole:
      'https://www.elastic.co/guide/en/enterprise-search/current/app-search-workplace-search-docs.html',
    userconsole:
      'https://www.elastic.co/guide/en/enterprise-search/current/app-search-workplace-search-docs.html',
    'heroku-userconsole':
      'https://www.elastic.co/guide/en/enterprise-search/current/app-search-workplace-search-docs.html',
  },
  KibanaApiGenerationKey: {
    adminconsole: 'https://www.elastic.co/docs/deploy-manage/api-keys/elasticsearch-api-keys',
    userconsole: 'https://www.elastic.co/docs/deploy-manage/api-keys/elasticsearch-api-keys',
    'heroku-userconsole':
      'https://www.elastic.co/docs/deploy-manage/api-keys/elasticsearch-api-keys',
  },
  updateEmailExistingDocLink: {
    adminconsole:
      'https://www.elastic.co/docs/cloud-account/update-your-email-address#sign-up-existing',
    userconsole:
      'https://www.elastic.co/docs/cloud-account/update-your-email-address#sign-up-existing',
    'heroku-userconsole':
      'https://www.elastic.co/docs/cloud-account/update-your-email-address#sign-up-existing',
  },
} satisfies Record<string, DocLinkDefinition>

export default docLinks
